import { ChangeEvent, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { skipToken } from '@reduxjs/toolkit/query'
import { t, Trans } from '@lingui/macro'
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined'
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined'
import DoNotDisturbAltOutlinedIcon from '@mui/icons-material/DoNotDisturbAltOutlined'
import ForwardToInboxOutlinedIcon from '@mui/icons-material/ForwardToInboxOutlined'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined'
import { Alert, AlertTitle, Button, Dialog, DialogActions, DialogContent, SpeedDial, SpeedDialAction, Stack, Typography } from '@mui/material'

import HeartbeatLoader from '@/components/ui/loaders/HeartbeatLoader'
import AccessDenied from '@/components/ui/template/AccessDenied'
import { useGetConsultationQuery } from '@/core/services/consultation'
import { useGetPatientQuery } from '@/core/services/patient'
import { useIdentityStatus } from '@/entities/patient'
import { EfficienceWebIframe } from '@/features/dmp-connect'
import { FeatureFlag, useIsFlagEnabled } from '@/hooks/useIsFlagEnabled'
import { selectDMPConnectTlsiConnectorId } from '@/redux/reducers/dmp-connect.reducer'
import { useAppSelector } from '@/redux/rootReducer'

import { fileToBase64 } from '../lib/helpers'
import { useDmpFlow } from '../model/useDMPFlow'
import { useMssFlow } from '../model/useMssFlow'
import { SendMssMessageToPatientOptions, useSendMssMessageToPatient } from '../model/useSendMssMessageToPatient'
import { useMessageEvent } from '../model/useMessageEvent'
import NotFoundPage from '@/pages/error/NotFoundPage'
import { Patient, PatientIdentityStatus } from '@/core/models/patient.model'
import { useIsINSEnabled } from '@/shared/model/useIsINSEnabled'

export function EfficienceWebPage() {
  const [searchParams] = useSearchParams()
  const consultationId = searchParams.get('consultationId')
  const patientIdParam = searchParams.get('patientId')
  const mode = searchParams.get('mode')

  const isDmpConnectEnabled = useIsFlagEnabled(FeatureFlag.DmpConnect)

  const tlsiConnectorId = useAppSelector(selectDMPConnectTlsiConnectorId)

  const { data: consultation, isLoading: isLoadingConsultation, isError: isConsultationError } = useGetConsultationQuery(consultationId ?? skipToken)

  // Resolve patient ID: either from consultation or directly from URL param
  const resolvedPatientId = consultation?.patient._id ?? patientIdParam
  const { data: patient, isLoading: isLoadingPatient, isError: isPatientError } = useGetPatientQuery(resolvedPatientId ?? skipToken)
  const { isINSEnabled } = useIsINSEnabled(patient)

  if (!isDmpConnectEnabled || !isINSEnabled || !tlsiConnectorId) {
    return (
      <div className="w-screen h-screen flex flex-col items-center justify-center">
        <AccessDenied title={t`DMP Connect`} description={t`DMP service is not available`}>
          <Button component={Link} to={'/consultations'} variant="contained">
            <Trans>Back</Trans>
          </Button>
        </AccessDenied>
      </div>
    )
  }

  const isLoading = isLoadingPatient || (consultationId ? isLoadingConsultation : false)
  const isError = isPatientError || (consultationId ? isConsultationError : false)

  if (isLoading) {
    return <PageLoader />
  }

  if (isError) {
    return <PageError />
  }

  if (!patient) return <NotFoundPage />

  return <EfficienceWeb patient={patient} mssClientMode={mode === 'mss'} />
}

function EfficienceWeb({ patient, mssClientMode }: { patient: Patient; mssClientMode: boolean }) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [connectedMssAddress, setConnectedMssAddress] = useState<string>()
  const [showInsNotQualified, setShowInsNotQualified] = useState(false)

  const { identityStatus } = useIdentityStatus(patient._id)

  const { dmpStatus, setDmpStatus, testDmpReqId, openDmpReqId } = useDmpFlow({
    iframe: iframeRef.current,
    isLoggedIn,
    patientInsNIR: patient.insNIR,
    mssClientMode,
  })

  const { getStatusReqId } = useMssFlow({
    iframe: iframeRef.current,
    isLoggedIn,
    patientInsNIR: patient.insNIR,
    patientLastname: patient.userInfo.surname,
    patientFirstname: patient.userInfo.name,
    mssClientMode,
  })

  const { sendMssMessageToPatient, canSend } = useSendMssMessageToPatient({
    iframe: iframeRef.current,
    patientInsNIR: patient.insNIR,
    patientLastname: patient.userInfo.surname,
    patientFirstname: patient.userInfo.name,
    senderMssAddress: connectedMssAddress,
  })

  const { onMessageEvent } = useMessageEvent({
    patientId: patient._id,
    dmpStatus,
    setDmpStatus,
    testDmpReqId,
    openDmpReqId,
    getStatusReqId,
    onMssAccountEmail: setConnectedMssAddress,
  })

  // Sending to the patient's "Mon espace santé" mailbox requires a qualified
  // INS identity (MSS.07): block the send and inform the practitioner otherwise.
  const sendToPatientIfInsQualified = (options?: SendMssMessageToPatientOptions) => {
    if (identityStatus !== PatientIdentityStatus.QUALIFIED) {
      setShowInsNotQualified(true)
      return
    }
    sendMssMessageToPatient(options)
  }

  const onDocumentSelected = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return

    const contentBase64 = await fileToBase64(file)
    sendToPatientIfInsQualified({ documents: [{ title: file.name, contentBase64 }] })
  }

  return (
    <div className="h-screen relative">
      {mssClientMode && isLoggedIn && canSend && (
        <SpeedDial
          ariaLabel={t`Message the patient`}
          direction="up"
          icon={<AddCommentOutlinedIcon />}
          FabProps={{ color: 'primary', size: 'medium' }}
          sx={{ position: 'absolute', bottom: 16, left: 16, zIndex: 10, '& .MuiSpeedDial-actions': { pb: 1 } }}
        >
          <SpeedDialAction icon={<MailOutlineIcon />} tooltipTitle={t`New message to patient`} tooltipOpen onClick={() => sendToPatientIfInsQualified()} />
          <SpeedDialAction
            icon={<DoNotDisturbAltOutlinedIcon />}
            tooltipTitle={t`New message — no reply (end conversation)`}
            tooltipOpen
            onClick={() => sendToPatientIfInsQualified({ forbidRecipientAnswer: true })}
          />
          <SpeedDialAction
            icon={<AttachFileOutlinedIcon />}
            tooltipTitle={t`Send a health document to patient`}
            tooltipOpen
            onClick={() => fileInputRef.current?.click()}
          />
          {connectedMssAddress && (
            <SpeedDialAction
              icon={<MarkEmailReadOutlinedIcon />}
              tooltipTitle={t`New message — request delivery receipt`}
              tooltipOpen
              onClick={() => sendToPatientIfInsQualified({ returnReceipt: true })}
            />
          )}
          {connectedMssAddress && (
            <SpeedDialAction
              icon={<ForwardToInboxOutlinedIcon />}
              tooltipTitle={t`New automatic message (reply to my mailbox)`}
              tooltipOpen
              onClick={() => sendToPatientIfInsQualified({ automaticNotice: true })}
            />
          )}
        </SpeedDial>
      )}
      <input ref={fileInputRef} type="file" accept="application/pdf" onChange={onDocumentSelected} hidden />
      <Dialog open={showInsNotQualified} onClose={() => setShowInsNotQualified(false)} maxWidth="sm">
        <DialogContent>
          <Alert severity="error">
            <AlertTitle>
              <Trans>MSSanté sending blocked</Trans>
            </AlertTitle>
            <Trans>
              The patient's INS national health identity is not qualified. Sending to the patient's "Mon espace santé" mailbox requires a qualified INS
              identity.
            </Trans>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowInsNotQualified(false)}>
            <Trans>Close</Trans>
          </Button>
        </DialogActions>
      </Dialog>
      <EfficienceWebIframe
        mssClientMode={mssClientMode}
        onLogin={(loginMssAddress) => {
          setIsLoggedIn(true)
          // The login payload rarely carries the mailbox address; getStatus is
          // the reliable source. Only overwrite when the payload has one.
          if (loginMssAddress) setConnectedMssAddress(loginMssAddress)
        }}
        onMessageEvent={onMessageEvent}
        ref={iframeRef}
      />
    </div>
  )
}

function PageLoader() {
  return (
    <Stack flex={1} alignItems="center" justifyContent="center" height="100vh">
      <HeartbeatLoader sx={{ transform: 'scale(4)' }} />
    </Stack>
  )
}

function PageError() {
  return (
    <Stack flex={1} alignItems="center" justifyContent="center" height="100vh" gap={2}>
      <Typography color="error">
        <Trans>Failed to load consultation or patient data</Trans>
      </Typography>
      <Button component={Link} to={'/consultations'} variant="contained">
        <Trans>Back</Trans>
      </Button>
    </Stack>
  )
}
